import { axiosClient } from '@/shared/apis/config';
import { ApiResponse } from '@/shared/types';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

// ============================================================
// YouTube 연동 상태 조회
// ============================================================

const youTubeStatusResSchema = z.object({
  connected: z.boolean(),
  channelId: z.string().nullable().optional(),
  channelName: z.string().nullable().optional(),
  customUrl: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  profileImageUrl: z.string().nullable().optional(),
});

export type YouTubeStatusRes = z.infer<typeof youTubeStatusResSchema>;

type YouTubeStatusResponse = ApiResponse<YouTubeStatusRes>;

export const getYouTubeStatusApi = async (): Promise<YouTubeStatusResponse> => {
  const response = await axiosClient.get<YouTubeStatusResponse>(
    '/v1.0/connections/youtube/status',
  );
  youTubeStatusResSchema.parse(response.data.data);
  return response.data;
};

export const QueryKeyYouTubeStatus = 'youtubeStatus' as const;

export const useGetYouTubeStatusApi = (
  queryOptions?: CustomQueryOptions<YouTubeStatusResponse>,
) => {
  const { queryKey, ...rest } = queryOptions ?? {};

  return useQuery({
    queryKey: [QueryKeyYouTubeStatus, ...(queryKey ?? [])],
    queryFn: getYouTubeStatusApi,
    ...rest,
  });
};

// ============================================================
// YouTube 연동 (OAuth 코드 교환)
// ============================================================

export interface ConnectYouTubeRequest {
  code: string;
  redirectUri: string;
}

const connectYouTubeResSchema = z.object({
  connected: z.boolean(),
  channelId: z.string().nullable().optional(),
  channelName: z.string().nullable().optional(),
  customUrl: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  profileImageUrl: z.string().nullable().optional(),
});

export type ConnectYouTubeRes = z.infer<typeof connectYouTubeResSchema>;

type ConnectYouTubeResponse = ApiResponse<ConnectYouTubeRes>;

export const connectYouTubeApi = async (
  data: ConnectYouTubeRequest,
): Promise<ConnectYouTubeResponse> => {
  const response = await axiosClient.post<ConnectYouTubeResponse>(
    '/v1.0/connections/youtube/connect',
    data,
  );
  connectYouTubeResSchema.parse(response.data.data);
  return response.data;
};

export const useConnectYouTubeApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: connectYouTubeApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeyYouTubeStatus] });
      queryClient.invalidateQueries({ queryKey: [QueryKeyConnections] });
    },
  });
};

// ============================================================
// YouTube 연동 해제
// ============================================================

export interface DisconnectYouTubeRequest {
  channelId?: string; // 특정 채널만 해제할 경우 지정
}

export const disconnectYouTubeApi = async (
  params?: DisconnectYouTubeRequest,
): Promise<ApiResponse<null>> => {
  const queryParams = params?.channelId
    ? `?channelId=${encodeURIComponent(params.channelId)}`
    : '';
  const response = await axiosClient.delete<ApiResponse<null>>(
    `/v1.0/connections/youtube/disconnect${queryParams}`,
  );
  return response.data;
};

export const useDisconnectYouTubeApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectYouTubeApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeyYouTubeStatus] });
      queryClient.invalidateQueries({ queryKey: [QueryKeyConnections] });
    },
  });
};

// ============================================================
// YouTube 업로드
// ============================================================

/**
 * YouTube 업로드 요청 인터페이스
 *
 * YouTube Data API v3 공식 문서:
 * - Videos: insert: https://developers.google.com/youtube/v3/docs/videos/insert
 * - Video Resource: https://developers.google.com/youtube/v3/docs/videos#resource
 *
 * 설정 가능한 필드:
 * - title, description, tags, categoryId, privacyStatus, madeForKids ✅
 *
 * API로 설정 불가능한 필드:
 * - 연령 제한: read-only (YouTube가 자동 판단 또는 YouTube Studio에서만 설정)
 */
export interface YouTubeUploadRequest {
  renderingHistoryId: number;
  title: string;
  description?: string;
  tags?: string[];
  categoryId?: string;
  privacyStatus?: 'private' | 'public' | 'unlisted';
  madeForKids?: boolean;
  channelId?: string; // 업로드할 YouTube 채널 ID (다중 계정 지원)
}

const youTubeUploadResSchema = z.object({
  success: z.boolean(),
  videoId: z.string().nullable().optional(),
  videoUrl: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});

export type YouTubeUploadRes = z.infer<typeof youTubeUploadResSchema>;

type YouTubeUploadResponse = ApiResponse<YouTubeUploadRes>;

export const uploadToYouTubeApi = async (
  data: YouTubeUploadRequest,
): Promise<YouTubeUploadResponse> => {
  const response = await axiosClient.post<YouTubeUploadResponse>(
    '/v1.0/connections/youtube/upload',
    data,
  );
  youTubeUploadResSchema.parse(response.data.data);
  return response.data;
};

export const useUploadToYouTubeApi = () => {
  return useMutation({
    mutationFn: uploadToYouTubeApi,
  });
};

// ============================================================
// 소셜 연동 목록 조회
// ============================================================

export const ConnectionProvider = {
  YOUTUBE: 'YOUTUBE',
  INSTAGRAM: 'INSTAGRAM',
  TIKTOK: 'TIKTOK',
  FACEBOOK: 'FACEBOOK',
  TWITTER: 'TWITTER',
} as const;

export type ConnectionProviderType =
  (typeof ConnectionProvider)[keyof typeof ConnectionProvider];

export const ConnectionCapability = {
  VIDEO_UPLOAD: 'VIDEO_UPLOAD',
  IMAGE_UPLOAD: 'IMAGE_UPLOAD',
  STORY_UPLOAD: 'STORY_UPLOAD',
  ANALYTICS_READ: 'ANALYTICS_READ',
  COMMENT_READ: 'COMMENT_READ',
} as const;

export type ConnectionCapabilityType =
  (typeof ConnectionCapability)[keyof typeof ConnectionCapability];

const connectionItemSchema = z.object({
  provider: z.string(),
  connected: z.boolean(),
  accountName: z.string().nullable().optional(),
  accountId: z.string().nullable().optional(),
  customUrl: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  profileImageUrl: z.string().nullable().optional(),
});

export type ConnectionItem = z.infer<typeof connectionItemSchema>;

const connectionsResSchema = z.object({
  connections: z.array(connectionItemSchema),
});

export type ConnectionsRes = z.infer<typeof connectionsResSchema>;

type ConnectionsResponse = ApiResponse<ConnectionsRes>;

export const getConnectionsApi = async (): Promise<ConnectionsResponse> => {
  const response =
    await axiosClient.get<ConnectionsResponse>('/v1.0/connections');
  connectionsResSchema.parse(response.data.data);
  return response.data;
};

export const QueryKeyConnections = 'connections' as const;

export const useGetConnectionsApi = (
  queryOptions?: CustomQueryOptions<ConnectionsResponse>,
) => {
  const { queryKey, ...rest } = queryOptions ?? {};

  return useQuery({
    queryKey: [QueryKeyConnections, ...(queryKey ?? [])],
    queryFn: getConnectionsApi,
    ...rest,
  });
};

// ============================================================
// 유틸리티 함수
// ============================================================

/**
 * 연동된 서비스만 필터링
 */
export const getConnectedServices = (connections: ConnectionItem[]) => {
  return connections.filter((c) => c.connected);
};

/**
 * 특정 provider의 연동 정보 조회 (첫 번째 연결 반환)
 */
export const getConnectionByProvider = (
  connections: ConnectionItem[],
  provider: ConnectionProviderType,
) => {
  return connections.find((c) => c.provider === provider && c.connected);
};

/**
 * 특정 provider의 모든 연동 정보 조회 (다중 계정 지원)
 */
export const getConnectionsByProvider = (
  connections: ConnectionItem[],
  provider: ConnectionProviderType,
) => {
  return connections.filter((c) => c.provider === provider && c.connected);
};

/**
 * Provider 정보 (아이콘, 표시명 등)
 */
export const PROVIDER_INFO: Record<
  ConnectionProviderType,
  { displayName: string; iconKey: string; color: string }
> = {
  YOUTUBE: { displayName: 'YouTube', iconKey: 'youtube', color: '#FF0000' },
  INSTAGRAM: {
    displayName: 'Instagram',
    iconKey: 'instagram',
    color: '#E4405F',
  },
  TIKTOK: { displayName: 'TikTok', iconKey: 'tiktok', color: '#000000' },
  FACEBOOK: { displayName: 'Facebook', iconKey: 'facebook', color: '#1877F2' },
  TWITTER: { displayName: 'X (Twitter)', iconKey: 'twitter', color: '#000000' },
};
